<template>
    <v-container fluid>
        <v-card class="pa-4">
            <!-- Заголовок -->
            <v-card-title class="text-h5 mb-4 d-flex flex-column align-start">
                <span>Результаты выверки</span>
                <span>с {{ formatDate(dateFrom) }} по {{ formatDate(dateTo) }}</span>
            </v-card-title>

            <!-- Таблица -->
            <v-data-table :headers="headers" :items="verificationResults" :loading="loading"
                loading-text="Загрузка данных..." no-data-text="Нет данных..." class="elevation-1" :items-per-page="-1"
                :footer-props="{
                    'items-per-page-options': [-1, 50, 20, 15, 10],
                    'items-per-page-text': 'Записей на странице:'
                }">

                <template v-slot:[`item.logisticCompanyName`]="{ item }">
                    {{ item.logisticCompanyName || 'Нет клиента' }}
                </template>

                <template v-slot:[`item.saldoBeg`]="{ item }">
                    {{ formatNumber(item.saldoBeg) }}
                </template>

                <template v-slot:[`item.calc`]="{ item }">
                    {{ formatNumber(item.calc) }}
                </template>

                <template v-slot:[`item.oplata`]="{ item }">
                    {{ formatNumber(item.oplata) }}
                </template>

                <template v-slot:[`item.saldoEnd`]="{ item }">
                    {{ formatNumber(item.saldoEnd) }}
                </template>

                <template v-slot:[`item.dolg`]="{ item }">
                    {{ formatNumber(item.dolg) }}
                </template>

                <template v-slot:[`item.details`]="{ item }">
                    <v-btn icon small color="primary" @click="showDetails(item)">
                        <v-icon>mdi-information</v-icon>
                    </v-btn>
                </template>

                <!-- Пустой слот для данных -->
                <template v-slot:no-data>
                    <div class="text-center pa-4">
                        <v-icon large color="grey lighten-2">mdi-file-document-outline</v-icon>
                        <div class="text-subtitle-1 mt-2">Нет данных для отображения</div>
                    </div>
                </template>
            </v-data-table>

            <!-- Итоговая строка с суммами по колонкам -->
            <v-row class="mt-4 px-3" v-if="verificationResults.length">
                <v-col cols="12">
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th class="text-left" style="width: 20%;">Показатель</th>
                                <th class="text-right" style="width: 16%;">Сальдо на начало</th>
                                <th class="text-right" style="width: 16%;">Начислено</th>
                                <th class="text-right" style="width: 16%;">Оплачено</th>
                                <th class="text-right" style="width: 16%;">Сальдо на конец</th>
                                <th class="text-right" style="width: 16%;">Долг</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style="font-weight: bold; background-color: #f5f5f5;">
                                <td class="text-left">Итого:</td>
                                <td class="text-right">{{ formatNumber(totalSaldoBeg) }}</td>
                                <td class="text-right">{{ formatNumber(totalCalc) }}</td>
                                <td class="text-right">{{ formatNumber(totalOplata) }}</td>
                                <td class="text-right">{{ formatNumber(totalSaldoEnd) }}</td>
                                <td class="text-right">{{ formatNumber(totalDolg) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>

            <!-- Панель экспорта -->
            <v-divider class="my-4" v-if="verificationResults.length"></v-divider>

            <v-row dense align="center" class="px-3" v-if="verificationResults.length">
                <v-col cols="auto">
                    <FileTypeSelector v-model="fileType" />
                </v-col>
                <v-col cols="auto">
                    <v-btn color="primary" :loading="exportLoading"
                        :disabled="!verificationResults.length || exportLoading" @click="exportClick">
                        <v-icon left>mdi-download</v-icon>
                        Экспортировать
                        <v-icon right v-if="fileType === 'pdf'">mdi-file-pdf</v-icon>
                        <v-icon right v-else>mdi-file-excel</v-icon>
                    </v-btn>
                </v-col>
                <v-col cols="auto">
                    <v-chip small color="info">
                        {{ verificationResults.length }} клиентов
                    </v-chip>
                </v-col>
                <v-col cols="auto" class="ml-auto">
                    <v-chip small color="grey" text-color="white">
                        <v-icon left small>mdi-calendar</v-icon>
                        {{ formatDate(dateFrom) }} — {{ formatDate(dateTo) }}
                    </v-chip>
                </v-col>
            </v-row>
        </v-card>
    </v-container>
</template>

<script>
import FileTypeSelector from '@/components/FileTypeSelector.vue';

export default {
    name: 'VerificationResultsDisplaying',

    components: {
        FileTypeSelector
    },

    data() {
        return {
            verificationResults: [],
            dateFrom: null,
            dateTo: null,
            exportLoading: false,
            fileType: 'xlsx',
            headers: [
                { text: 'Клиент', value: 'logisticCompanyName', sortable: true, align: 'start' },
                { text: 'Сальдо на начало', value: 'saldoBeg', sortable: true, align: 'end' },
                { text: 'Начислено', value: 'calc', sortable: true, align: 'end' },
                { text: 'Оплачено', value: 'oplata', sortable: true, align: 'end' },
                { text: 'Сальдо на конец', value: 'saldoEnd', sortable: true, align: 'end' },
                { text: 'Сумма просроченной оплаты', value: 'dolg', sortable: true, align: 'end' },
                { text: 'Детали', value: 'details', sortable: false, align: 'center' }
            ],
            loading: false
        }
    },

    computed: {
        totalSaldoBeg() {
            return this.verificationResults.reduce((sum, item) => {
                return sum + (Number(item.saldoBeg) || 0);
            }, 0);
        },
        totalCalc() {
            return this.verificationResults.reduce((sum, item) => {
                return sum + (Number(item.calc) || 0);
            }, 0);
        },
        totalOplata() {
            return this.verificationResults.reduce((sum, item) => {
                return sum + (Number(item.oplata) || 0);
            }, 0);
        },
        totalSaldoEnd() {
            return this.verificationResults.reduce((sum, item) => {
                return sum + (Number(item.saldoEnd) || 0);
            }, 0);
        },
        totalDolg() {
            return this.verificationResults.reduce((sum, item) => {
                return sum + (Number(item.dolg) || 0);
            }, 0);
        }
    },

    watch: {
        // Более надежный способ отслеживания
        '$route'(to, from) {
            if (to.query.dateFrom !== from?.query?.dateFrom || 
                to.query.dateTo !== from?.query?.dateTo) {
                this.loadVerificationResults();
            }
        }
    },
    
    created() {
        this.loadVerificationResults();
    },

    methods: {
        formatNumber(value) {
            if (value === null || value === undefined || isNaN(value)) return '0.00';
            return Number(value).toFixed(2);
        },

        formatDate(dateString) {
            if (!dateString) return '';

            if (dateString instanceof Date) {
                if (isNaN(dateString.getTime())) return '';
                return dateString.toLocaleDateString('ru-RU');
            }

            if (typeof dateString === 'string') {
                if (!dateString.trim() || dateString === 'null' || dateString === 'undefined') {
                    return '';
                }

                const parts = dateString.split('.');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);

                    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                        const date = new Date(year, month, day);
                        if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('ru-RU');
                        }
                    }
                }

                try {
                    const date = new Date(dateString);
                    if (!isNaN(date.getTime())) {
                        return date.toLocaleDateString('ru-RU');
                    }
                } catch (e) {
                    // Игнорируем ошибку
                }

                return '';
            }

            return '';
        },

        showDetails(item) {
            if (!this.dateFrom || !this.dateTo) {
                this.$notify?.('error', 'Даты периода не заданы');
                return;
            }

            const formatDateForQuery = (date) => {
                if (date instanceof Date) {
                    return date.toISOString().split('T')[0];
                }
                return date;
            };

            this.$router.push({
                name: 'VerificationDetails',
                query: {
                    dateFrom: formatDateForQuery(this.dateFrom),
                    dateTo: formatDateForQuery(this.dateTo),
                    saldoBeg: item.saldoBeg || 0,
                    logisticCompanyName: item.logisticCompanyName || '',
                    inn: item.inn || ''
                }
            });
        },

        async exportClick() {
            if (this.exportLoading) return;

            try {
                this.exportLoading = true;

                if (!this.verificationResults || !this.verificationResults.length) {
                    this.$notify?.('warning', 'Нет данных для экспорта');
                    return;
                }

                // Подготавливаем данные в формате VerificationRow
                const exportData = this.verificationResults.map(item => ({
                    logisticCompanyName: item.logisticCompanyName || '',
                    inn: item.inn || '',
                    kpp: item.kpp || '',
                    saldoBeg: Number(item.saldoBeg) || 0,
                    calc: Number(item.calc) || 0,
                    oplata: Number(item.oplata) || 0,
                    saldoEnd: Number(item.saldoEnd) || 0,
                    dolg: Number(item.dolg) || 0
                }));

                // Форматируем даты
                const formatDateForQuery = (date) => {
                    if (!date) return null;
                    if (date instanceof Date) {
                        return date.toISOString();
                    }
                    return new Date(date).toISOString();
                };

                // Создаем объект запроса
                const requestBody = {
                    dateFrom: formatDateForQuery(this.dateFrom),
                    dateTo: formatDateForQuery(this.dateTo),
                    fileType: this.fileType || 'xlsx',
                    exportData: exportData
                };

                // Отправляем запрос - НЕ указываем responseType: 'blob', так как сервер возвращает JSON с URL
                const response = await this.axios.post('/reports/Verification', requestBody);

                // Обработка ответа - сервер возвращает URL строку
                if (response.data) {
                    let fileUrl = null;

                    // Если пришла строка с URL
                    if (typeof response.data === 'string') {
                        fileUrl = response.data;
                    }
                    // Если пришел объект с URL
                    else if (response.data.url) {
                        fileUrl = response.data.url;
                    }
                    // Если пришел объект с result
                    else if (response.data.result) {
                        fileUrl = response.data.result;
                    }

                    if (fileUrl) {
                        // Открываем URL в новой вкладке для скачивания
                        window.open(fileUrl, '_blank');
                        this.$notify?.('success', 'Отчет успешно сформирован');
                    } else {
                        this.$notify?.('error', 'Не удалось получить ссылку на отчет');
                    }
                } else {
                    this.$notify?.('error', 'Не удалось получить ссылку на отчет');
                }

            } catch (error) {
                console.error('Export error:', error);
                this.$notify?.(
                    'error',
                    error.response?.data?.message || 'Ошибка при экспорте данных'
                );
            } finally {
                this.exportLoading = false;
            }
        },

        async loadVerificationResults() {
            try {
                this.loading = true;

                const query = this.$route.query;

                const parseDate = (dateStr) => {
                    if (!dateStr) return null;
                    const date = new Date(dateStr);
                    return isNaN(date.getTime()) ? null : date;
                };

                this.dateFrom = parseDate(query.dateFrom);
                this.dateTo = parseDate(query.dateTo);

                const formatDateForServer = (date) => {
                    if (!date) return null;
                    return date.toISOString();
                };

                const response = await this.axios.get('/reports/Verification', {
                    params: {
                        dateFrom: formatDateForServer(this.dateFrom),
                        dateTo: formatDateForServer(this.dateTo)
                    }
                });

                let results = [];
                if (Array.isArray(response.data)) {
                    results = response.data;
                } else if (response.data && typeof response.data === 'object') {
                    if (Array.isArray(response.data.items)) {
                        results = response.data.items;
                    } else if (Array.isArray(response.data.verificationResults)) {
                        results = response.data.verificationResults;
                    } else if (Array.isArray(response.data.data)) {
                        results = response.data.data;
                    } else if (response.data.$values) {
                        results = response.data.$values || [];
                    } else {
                        results = [response.data];
                    }
                }

                this.verificationResults = results;

                // Принудительно обновляем представление
                this.$forceUpdate();

            } catch (err) {
                console.error('Error loading verification results:', err);
                this.$notify?.(
                    'error',
                    err.response?.data?.message || 'Ошибка при загрузке результатов выверки'
                );
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
.v-data-table>>>.v-data-table__wrapper {
    overflow-x: auto;
}

/* Выравнивание заголовков таблицы */
.v-data-table>>>th {
    white-space: nowrap;
}

/* Выравнивание ячеек */
.text-right {
    text-align: right !important;
}

.text-left {
    text-align: left !important;
}

.text-center {
    text-align: center !important;
}

/* Стили для итоговой таблицы */
.v-simple-table {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
}

.v-simple-table th,
.v-simple-table td {
    padding: 8px 16px !important;
}

.v-simple-table thead th {
    background-color: #f5f5f5;
    font-weight: 600;
    border-bottom: 2px solid #e0e0e0;
}

.v-simple-table tbody tr:last-child td {
    border-top: 2px solid #e0e0e0;
}
</style>